import BugReport from 'components/BugReport'
import IconRender from 'components/common/IconRender'
import MobileNavBar from 'components/header/MobileNavBar'
import UserMenu from 'components/header/UserMenu'
import LanguageSwitcher from 'components/language/LanguageSelector'
import { isMobile } from 'components/utils'
import { useAuth } from 'pages/authenticated/AuthContext'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { CustomColorsRef, IconSizes } from 'types/general'
import { NavigationItemType } from 'types/navigation'
import { StatusType } from 'types/status'

import AppLogo from './AppLogo'
import FlatHeaderButton from './FlatHeaderButton'
import HeaderButton from './HeaderButton'

const HEADER_NAV_ITEMS: { navigate: string; labelKey: string }[] = [
  { navigate: '/create-ad', labelKey: 'home.transferMyLease' },
  { navigate: '/listings', labelKey: 'home.takeOverLease' },
  { navigate: '#comment-ca-marche', labelKey: 'home.howItWorks' },
]

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuth()
  const { role } = usePermissions()
  const [isOpen, setIsOpen] = useState(false)
  const mobile = isMobile()
  const { t } = useTranslation()

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const handleNavClick = (item: (typeof HEADER_NAV_ITEMS)[0]) => {
    if (item.navigate.startsWith('#')) {
      if (location.pathname !== '/') navigate('/')
      window.location.hash = item.navigate.slice(1)
    } else if (item.navigate === '/create-ad') {
      navigate('/create-ad', { state: { newListing: true } })
    } else {
      navigate(item.navigate)
    }
  }

  const mobileNavItems: NavigationItemType[] = HEADER_NAV_ITEMS.map(
    ({ navigate, labelKey }) => ({
      navigate,
      label: labelKey,
      icon: '',
      openIcon: '',
      iconStatus: StatusType.info,
      iconStatusCustomClass: '',
    })
  )
  if (isAuthenticated) {
    if (role === 'support' || role === 'admin') {
      mobileNavItems.push({
        navigate: '/support-console',
        label: 'common.supportConsole',
        icon: '',
        openIcon: '',
        iconStatus: StatusType.info,
        iconStatusCustomClass: '',
      })
    }
    if (role === 'admin') {
      mobileNavItems.push({
        navigate: '/admin-console',
        label: 'common.adminConsole',
        icon: '',
        openIcon: '',
        iconStatus: StatusType.info,
        iconStatusCustomClass: '',
      })
    }
  }

  return (
    <>
      <header className="sticky top-0 z-50 bg-imovWhite shadow-sm">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4">
          <div className="flex items-center gap-2">
            <AppLogo variant="light" iconHeight="h-9" />
          </div>

          {/* Desktop: nav */}
          <nav className="hidden items-center gap-8 text-sm font-medium text-imovSecondary md:flex">
            <BugReport />
            {[
              ...HEADER_NAV_ITEMS,
              ...((isAuthenticated && (role === 'support' || role === 'admin'))
                ? [{ navigate: '/support-console', labelKey: 'common.supportConsole' }]
                : []),
              ...((isAuthenticated && role === 'admin')
                ? [{ navigate: '/admin-console', labelKey: 'common.adminConsole' }]
                : []),
            ].map((item, index) => (
              <FlatHeaderButton
                key={index}
                variant="light"
                handleClick={() => handleNavClick(item)}
                isSelected={
                  item.navigate.startsWith('#')
                    ? false
                    : location.pathname === item.navigate
                }
              >
                {t(item.labelKey)}
              </FlatHeaderButton>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <UserMenu compact={mobile} />
            ) : (
              <div className="hidden items-center gap-4 md:flex">
                <LanguageSwitcher />
                <HeaderButton
                  variant="text"
                  handleClick={() =>
                    navigate('/auth', {
                      state: { from: location },
                    })
                  }
                >
                  {t('common.logIn')}
                </HeaderButton>
                <HeaderButton
                  variant="primary"
                  handleClick={() =>
                    navigate('/create-user', {
                      state: { from: location },
                    })
                  }
                >
                  {t('home.start')}
                </HeaderButton>
              </div>
            )}

            {mobile ? (
              <button
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleMenu()
                }}
                className="rounded-lg p-2 text-imovPrimary outline-none transition-colors duration-200 touch-manipulation focus:outline-none md:hidden"
                style={{ minWidth: '48px', minHeight: '48px', zIndex: 1000 }}
                type="button"
                aria-label={isOpen ? t('common.close') : t('common.openMenu')}
              >
                <IconRender
                  icon={isOpen ? 'regular-xmark' : 'regular-bars'}
                  size={IconSizes.LG}
                  color={CustomColorsRef.Primary}
                />
              </button>
            ) : null}
          </div>
        </div>
        {mobile ? (
          <MobileNavBar
            navigationItems={mobileNavItems}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
          />
        ) : null}
      </header>
    </>
  )
}

export default Header
